// DeleteConfirmModal.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { getModalStyles, getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { captureError } from "../../utils/sentry";
import { TOKENS } from "../../utils/designTokens";

type DeleteConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void; // allow async
  title?: string;
  message?: string;
};

const ConfirmNotesDeleteModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation(['modals', 'common']);
  const [loading, setLoading] = useState(false);

  const displayTitle = title || t('modals:confirmDelete.title');
  const displayMessage = message || t('modals:confirmDelete.message');

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm(); // call parent delete handler
      onClose(); // close after success
    } catch (error) {
      captureError(error, { component: "ConfirmNotesDeleteModal", operation: "delete" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 ${TOKENS.overlay.backdrop}`}>
      <div className={`${getModalStyles(isDarkMode)} rounded-lg shadow-lg p-6 w-96`}>
        <h2 className={`text-lg font-semibold mb-4 ${getPrimaryText(isDarkMode)}`}>{displayTitle}</h2>
        <p className={`mb-6 ${getSecondaryText(isDarkMode)}`}>{displayMessage}</p>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className={`px-4 py-2 rounded disabled:opacity-50 ${
              isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded text-white flex items-center justify-center disabled:opacity-50 ${
              isDarkMode
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {loading ? (
              <div className="flex gap-2 items-center">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
               <span>{t('modals:confirmDelete.deleting')}</span>
              </div>
            ) : (
              t('common:buttons.delete')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmNotesDeleteModal;
